import {
  useToast,
  Button,
  ButtonProps,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import axios from 'axios';
import FileSaver from 'file-saver';
import React, { useCallback, useState, useEffect } from 'react';

import {
  InputTypeGQLType,
  ReceiptZipFileDownloadAvailableGQLType,
} from '../../../../../types/dashboard-core-gqlTypes.generated.d';
import { useGetAreDeliverablesReceiptsZipFilesAvailableForDownloadQuery } from '~/core/routes/deliverables/[deliverableId]/queries/GetAreDeliverablesReceiptsZipFilesAvailableForDownload.gql.generated';
import { useGetDeliverableArtifactInstanceDownloadLazyQueryGQLType } from '~/core/routes/deliverables/[deliverableId]/queries/GetDeliverableArtifactInstanceDownload.gql.generated';
import { GetDeliverableContainerReceiptsQueryGQLType } from '~/core/routes/deliverables/[deliverableId]/queries/GetDeliverableContainerReceipts.gql.generated';
import { useGetDeliverablesContainerReceiptZipFilesDownloadUrlLazyQueryGQLType } from '~/core/routes/deliverables/[deliverableId]/queries/GetDeliverablesContainerReceiptZipFilesDownloadUrl.gql.generated';
import { IconButton } from '~/packages/common-frontend/components/IconButton';
import { Icon } from '~/packages/common-frontend/components/icons';
import {
  successToastConfig,
  errorToastId,
  errorToastConfig,
} from '~/packages/common-frontend/constants';
import { getUnexpectedErrorMessage } from '~/packages/common-frontend/utils';

// Hooks
type Args = { receiptId: string };
const useCanDownloadZipFiles = ({ receiptId }: Args) => {
  const toast = useToast();
  const {
    data: areZipFilesAvailableData,
    startPolling: startAreZipFilesAvailablePolling,
    stopPolling: stopAreZipFilesAvailablePolling,
  } = useGetAreDeliverablesReceiptsZipFilesAvailableForDownloadQuery({
    variables: { receiptId },
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
  });

  const [
    canDownloadZipFiles,
    setCanDownloadZipFiles,
  ] = useState<ReceiptZipFileDownloadAvailableGQLType>();

  // can download zip files is not updating when we switch between receipts, so forcing that here
  useEffect(() => {
    setCanDownloadZipFiles(undefined);
  }, [receiptId]);

  useEffect(() => {
    if (areZipFilesAvailableData) {
      const canDownload =
        areZipFilesAvailableData.areDeliverablesReceiptsZipFilesAvailableForDownload;
      // if input is downloadable, show zip file download
      if (canDownload.canDownloadInput) {
        stopAreZipFilesAvailablePolling();
        setCanDownloadZipFiles(canDownload);
      } else {
        startAreZipFilesAvailablePolling(5000);
      }
    }
    return () => {
      stopAreZipFilesAvailablePolling();
    };
  }, [
    areZipFilesAvailableData,
    startAreZipFilesAvailablePolling,
    stopAreZipFilesAvailablePolling,
  ]);

  return canDownloadZipFiles;
};

// Fragments
type DownloadButtonProps = ButtonProps & {
  artifactInstanceId: string;
  iconOnly?: boolean;
  text?: string;
};
export const DownloadButton = ({
  artifactInstanceId,
  iconOnly,
  text,
  ...buttonProps
}: DownloadButtonProps) => {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [
    getDeliverableArtifactInstanceDownload,
  ] = useGetDeliverableArtifactInstanceDownloadLazyQueryGQLType({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
      setLoading(false);
    },
    fetchPolicy: 'no-cache',
    onCompleted: (response) => {
      if (response && response.deliverableArtifactInstanceDownload) {
        const { deliverableArtifactInstanceDownload } = response;
        deliverableArtifactInstanceDownload.forEach((downloadResponse, idx) => {
          if (downloadResponse.__typename === 'ArtifactInstanceDownloadUrl') {
            const url = downloadResponse.signedUrl;
            const filename = downloadResponse.filename;
            axios({
              url,
              method: 'GET',
              responseType: 'blob',
            })
              .then((res) => {
                const fileBlob = new Blob([res.data], {
                  type: res.data.type,
                });
                FileSaver.saveAs(fileBlob, `${idx}-${filename}`);
                setLoading(false);
              })
              .catch(() => {
                setLoading(true);
                if (!toast.isActive(errorToastId)) {
                  errorToastConfig.title = `Error downloading ${filename} [${idx}]`;
                  toast(errorToastConfig);
                }
              });
          }
          if (
            downloadResponse.__typename === 'ArtifactInstanceDownloadObject'
          ) {
            const object = downloadResponse.object;
            const filename = downloadResponse.filename;
            const blob = new Blob([JSON.stringify(object)], {
              type: 'application/json',
            });
            const url = window.URL.createObjectURL(blob);

            const tag = document.createElement('a');
            tag.setAttribute('href', url);
            tag.setAttribute('download', filename);
            tag.click();
            setLoading(false);
          }
        });
      }
    },
  });

  const onClick = useCallback(() => {
    setLoading(true);
    Sentry.addBreadcrumb({
      category: 'ui.click',
      message: `Clicked download for deliverable artifact instance id ${artifactInstanceId} v4 deliverables [iconOnly: ${
        iconOnly ?? false
      }]`,
      level: Sentry.Severity.Info,
    });
    getDeliverableArtifactInstanceDownload({
      variables: { artifactInstanceId },
    });
  }, [iconOnly, getDeliverableArtifactInstanceDownload, artifactInstanceId]);

  if (iconOnly) {
    return (
      <IconButton
        icon={<Icon icon="arrow-down-to-bracket" color="blue.400" />}
        isLoading={loading}
        colorScheme="blue"
        ariaLabel="download-file"
        label="Download File"
        onClick={onClick}
      />
    );
  }

  return (
    <Button
      isLoading={loading}
      colorScheme="blue"
      leftIcon={<Icon icon="arrow-down-to-bracket" boxSize={4} />}
      variant="ghost"
      onClick={onClick}
      {...buttonProps}
    >
      {text ?? 'Download'}
    </Button>
  );
};

type DownloadMenuZipFilesProps = ButtonProps & {
  fileName: string;
  receiptId: string;
  canDownloadZipFiles: ReceiptZipFileDownloadAvailableGQLType;
};
const DownloadMenuZipFiles = ({
  fileName: inputFileName,
  receiptId,
  canDownloadZipFiles,
  ...buttonProps
}: DownloadMenuZipFilesProps) => {
  const toast = useToast();

  const [isInput, setIsInput] = useState<boolean>(false);

  const [
    deliverablesContainerReceiptsZipFilesDownloadUrl,
  ] = useGetDeliverablesContainerReceiptZipFilesDownloadUrlLazyQueryGQLType({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    fetchPolicy: 'network-only',
    onCompleted: (response) => {
      if (
        response &&
        response.deliverablesContainerReceiptZipFilesDownloadUrl
      ) {
        const fileName = isInput
          ? `${inputFileName}-inputs.zip`
          : `${inputFileName}.zip`;
        const url = response.deliverablesContainerReceiptZipFilesDownloadUrl;
        axios({
          url,
          method: 'GET',
          responseType: 'blob',
        })
          .then((res) => {
            const fileBlob = new Blob([res.data], {
              type: res.data.type,
            });
            FileSaver.saveAs(fileBlob, fileName);
            successToastConfig.title = `Successfully downloaded ${fileName}`;
            toast(successToastConfig);
          })
          .catch(() => {
            if (!toast.isActive(errorToastId)) {
              errorToastConfig.title = `Error downloading ${fileName}`;
              toast(errorToastConfig);
            }
          });
      }
    },
  });

  const onClick = useCallback(
    (type: InputTypeGQLType) => {
      Sentry.addBreadcrumb({
        category: 'ui.click',
        message: `Clicked download for ${inputFileName} v4 deliverables ${type} zip files`,
        level: Sentry.Severity.Info,
      });
      if (type === 'input') {
        setIsInput(true);
      } else {
        setIsInput(false);
      }
      deliverablesContainerReceiptsZipFilesDownloadUrl({
        variables: {
          receiptId,
          type,
        },
      });
    },
    [
      inputFileName,
      deliverablesContainerReceiptsZipFilesDownloadUrl,
      receiptId,
    ],
  );

  return (
    <Menu>
      <MenuButton as={Button} {...buttonProps}>
        Download
      </MenuButton>
      <MenuList width="300px">
        {canDownloadZipFiles.canDownloadOutput && (
          <MenuItem onClick={() => onClick('output')}>Report</MenuItem>
        )}
        {canDownloadZipFiles.canDownloadInput && (
          <MenuItem onClick={() => onClick('input')}>All Inputs</MenuItem>
        )}
      </MenuList>
    </Menu>
  );
};

// Exports
type DownloadActionButtonProps = {
  currentReceipt: GetDeliverableContainerReceiptsQueryGQLType['deliverableContainerReceipts'][number];
};
export const DownloadActionButton = ({
  currentReceipt,
}: DownloadActionButtonProps) => {
  const receiptId = currentReceipt.id;
  const canDownloadZipFiles = useCanDownloadZipFiles({ receiptId });

  const lastFinalArtifact =
    currentReceipt.finalArtifactsInstances.length > 0
      ? currentReceipt.finalArtifactsInstances[
          currentReceipt.finalArtifactsInstances.length - 1
        ]
      : null;
  const filename = `${currentReceipt.creditFacilitySlug}-${currentReceipt.containerType}-${currentReceipt.containerDueDate}-v${currentReceipt.version}`;
  const lastFinalArtifactFilename = lastFinalArtifact
    ? `${filename}-${lastFinalArtifact.artifactConfigKey}-${lastFinalArtifact.artifactReportingDate}`
    : null;

  return (
    <>
      {canDownloadZipFiles && (
        <DownloadMenuZipFiles
          flexShrink={0}
          variant="secondary"
          colorScheme="v2-aurora"
          rightIcon={<Icon icon="chevron-down" />}
          fileName={filename}
          receiptId={receiptId}
          canDownloadZipFiles={canDownloadZipFiles}
        />
      )}
      {!canDownloadZipFiles &&
        lastFinalArtifact &&
        lastFinalArtifactFilename && (
          <DownloadButton
            flexShrink={0}
            variant="secondary"
            colorScheme="v2-aurora"
            text="Download"
            artifactInstanceId={lastFinalArtifact.receiptInstanceId}
          />
        )}
    </>
  );
};
